import React, { useState } from "react";
import {
    Box, Typography, CircularProgress, Card, CardContent, CardMedia,
    Chip, TextField, InputAdornment, Pagination, Button, Stack,
} from "@mui/material";
import { Search, FileCheck2, Download, BookOpen, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getResources } from "../api/resources";
import PageShell from "../components/layout/PageShell";
import SectionHeading from "../components/ui/SectionHeading";
import { fadeUp } from "../utils/animations";
import { resolveMediaUrl } from "../utils/mediaUrl";

const THUMB_HEIGHT = 168;

// A single resource card, sized identically regardless of how much (or how
// little) content the item has: fixed-height thumbnail slot (with a fallback
// graphic when there's no image), 2-line-clamped title/excerpt so text length
// never changes the card's height, and a footer action that's always present.
function ResourceCard({ item, index }) {
    const navigate = useNavigate();
    const [imgError, setImgError] = useState(false);
    const thumbnailSrc = resolveMediaUrl(item.thumbnail_url || item.thumbnail);
    const hasThumbnail = Boolean(thumbnailSrc) && !imgError;

    const goToDetail = () => navigate(`/resources/${item.slug}`);

    return (
        <Box sx={{ display: "flex", minWidth: 0 }}>
            <motion.div {...fadeUp(index * 0.03)} style={{ width: "100%", minWidth: 0 }}>
                <Card
                    onClick={goToDetail}
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "16px",
                        border: "1px solid rgba(15,23,42,0.08)",
                        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                        cursor: "pointer",
                        transition: "transform 0.25s cubic-bezier(.2,.8,.2,1), box-shadow 0.25s ease, border-color 0.25s ease",
                        "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: "0 22px 44px -22px rgba(37,99,235,0.32)",
                            borderColor: "rgba(37,99,235,0.28)",
                        },
                        "&:hover .res-icon": {
                            background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                            color: "white",
                        },
                    }}
                >
                    {/* Thumbnail slot — always the same height, real image or a
                        branded fallback so cards never differ because one item
                        happens to have art and another doesn't. */}
                    {hasThumbnail ? (
                        <CardMedia
                            component="img"
                            src={thumbnailSrc}
                            alt={item.title}
                            sx={{ height: THUMB_HEIGHT, objectFit: "cover", flexShrink: 0 }}
                            onError={() => setImgError(true)}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: THUMB_HEIGHT,
                                flexShrink: 0,
                                background: "linear-gradient(135deg, #0A0F1E, #1D4ED8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <BookOpen size={34} color="rgba(255,255,255,0.35)" strokeWidth={1.6} />
                        </Box>
                    )}

                    <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, minHeight: 42 }}>
                            <Box
                                className="res-icon"
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "12px",
                                    bgcolor: "rgba(37,99,235,0.08)",
                                    color: "#2563EB",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.25s ease",
                                    flexShrink: 0,
                                }}
                            >
                                <FileCheck2 size={20} strokeWidth={2.1} />
                            </Box>
                            {item.category_name && (
                                <Chip
                                    label={item.category_name}
                                    size="small"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: 10.5,
                                        letterSpacing: "0.03em",
                                        textTransform: "uppercase",
                                        color: "#2563EB",
                                        bgcolor: "rgba(37,99,235,0.07)",
                                        border: "1px solid rgba(37,99,235,0.14)",
                                        height: 22,
                                    }}
                                />
                            )}
                        </Box>

                        {/* Title — clamped to 2 lines, height reserved for 2
                            lines even for short titles, so this block is
                            identical across every card. */}
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.05rem",
                                color: "#0F172A",
                                letterSpacing: "-0.01em",
                                lineHeight: 1.35,
                                minHeight: "2.7em",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                mb: 1,
                            }}
                        >
                            {item.title}
                        </Typography>

                        {/* Excerpt — same idea: reserved 2-line height whether
                            or not this item actually has an excerpt. */}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                mb: 1,
                                lineHeight: 1.5,
                                minHeight: "3em",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {item.excerpt || ""}
                        </Typography>

                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block", mb: 2, fontSize: 12.5, minHeight: "1.4em" }}
                        >
                            {item.publish_date ? dayjs(item.publish_date).format("DD MMM YYYY") : ""}
                        </Typography>

                        {/* Footer action — always present, so the bottom edge
                            of every card lines up regardless of whether the
                            item has a downloadable file. */}
                        <Box sx={{ mt: "auto" }}>
                            {item.file_url ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Download size={14} />}
                                    component="a"
                                    href={item.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        borderRadius: "10px",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        fontSize: 13,
                                        borderColor: "rgba(37,99,235,0.3)",
                                        color: "#2563EB",
                                        "&:hover": { borderColor: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" },
                                    }}
                                >
                                    Download
                                </Button>
                            ) : (
                                <Button
                                    size="small"
                                    variant="text"
                                    endIcon={<ArrowRight size={14} />}
                                    onClick={(e) => { e.stopPropagation(); goToDetail(); }}
                                    sx={{
                                        borderRadius: "10px",
                                        fontWeight: 700,
                                        textTransform: "none",
                                        fontSize: 13,
                                        px: 0,
                                        color: "#2563EB",
                                        "&:hover": { bgcolor: "transparent", gap: 1 },
                                    }}
                                >
                                    Read More
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </motion.div>
        </Box>
    );
}

export default function ResourcesPage() {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const { data, isLoading } = useQuery({
        queryKey: ["resources", { search, page }],
        queryFn: () => getResources({
            ...(search && { search }),
            page,
            page_size: 12,
        }).then(r => r.data),
    });

    const resources = data?.results ?? [];
    const total = data?.total ?? 0;
    const pageCount = Math.ceil(total / 12);

    return (
       
        <PageShell>
            {/* Hero */}
            <Box sx={{ bgcolor: "#0A0F1E", pt: 16, pb: 11, position: "relative", overflow: "hidden", color: "white" }}>
                <Box className="tech-grid" sx={{ position: "absolute", inset: 0, opacity: 0.15, pointerEvents: "none" }} />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: "-20%",
                        left: "10%",
                        width: 480,
                        height: 480,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(37,99,235,0.3) 0%, transparent 70%)",
                        filter: "blur(10px)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        bottom: "-30%",
                        right: "0%",
                        width: 440,
                        height: 440,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(34,197,94,0.16) 0%, transparent 70%)",
                        filter: "blur(10px)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(180deg, transparent 0%, rgba(10,15,30,0.55) 88%, #0A0F1E 100%)",
                        pointerEvents: "none",
                    }}
                />

                <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative", textAlign: "center" }}>
                    <motion.div {...fadeUp()}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2.5 }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "8px",
                                    bgcolor: "rgba(37,99,235,0.16)",
                                    border: "1px solid rgba(37,99,235,0.35)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <BookOpen size={14} color="#60A5FA" strokeWidth={2.2} />
                            </Box>
                            <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 700, letterSpacing: "0.18em", fontSize: 12 }}>
                                Knowledge Center
                            </Typography>
                        </Stack>
                    </motion.div>

                    <motion.div {...fadeUp(0.08)}>
                        <Typography
                            variant="h2"
                            fontWeight={700}
                            sx={{ fontSize: { xs: "2rem", md: "3rem" }, letterSpacing: "-0.02em", lineHeight: 1.12, mb: 2 }}
                        >
                            Guides, whitepapers &{" "}
                            <Box
                                component="span"
                                sx={{
                                    background: "linear-gradient(90deg, #60A5FA, #93C5FD)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                resources
                            </Box>
                        </Typography>
                    </motion.div>

                    <motion.div {...fadeUp(0.14)}>
                        <Typography sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 400, fontSize: 17, lineHeight: 1.7, maxWidth: 600, mx: "auto", mb: 5 }}>
                            Certification guides, whitepapers, webinar recordings, and tech tutorials to accelerate your career.
                        </Typography>
                    </motion.div>

                    <motion.div {...fadeUp(0.2)}>
                        <Box sx={{ maxWidth: 500, mx: "auto" }}>
                            <TextField
                                fullWidth
                                placeholder="Search resources..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search size={18} color="#64748B" /></InputAdornment>,
                                    sx: {
                                        bgcolor: "white",
                                        borderRadius: "12px",
                                        fontSize: 15,
                                        boxShadow: "0 20px 40px -18px rgba(0,0,0,0.5)",
                                        "& fieldset": { border: "1px solid rgba(255,255,255,0.1)" },
                                        "&:hover fieldset": { borderColor: "#2563EB !important" },
                                        "&.Mui-focused fieldset": { borderColor: "#2563EB !important", borderWidth: "1.5px" },
                                    },
                                }}
                            />
                        </Box>
                    </motion.div>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ bgcolor: "#F8FAFC", py: 10 }}>
                <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 } }}>
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" py={10}><CircularProgress sx={{ color: "#2563EB" }} /></Box>
                    ) : resources.length === 0 ? (
                        <Box textAlign="center" py={10}>
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "16px",
                                    bgcolor: "rgba(37,99,235,0.07)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    mx: "auto",
                                    mb: 2,
                                }}
                            >
                                <Search size={24} color="#2563EB" />
                            </Box>
                            <Typography fontWeight={700} color="#0F172A" mb={0.5}>No resources found</Typography>
                            <Typography color="text.secondary" fontSize={14}>Try adjusting your search terms.</Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" mb={3} fontWeight={500}>
                                Showing {resources.length} of {total} resources
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                                    gap: 3,
                                }}
                            >
                                {resources.map((item, i) => (
                                    <ResourceCard key={item.id} item={item} index={i} />
                                ))}
                            </Box>
                            {pageCount > 1 && (
                                <Box display="flex" justifyContent="center" mt={6}>
                                    <Pagination
                                        count={pageCount}
                                        page={page}
                                        onChange={(_, v) => setPage(v)}
                                        color="primary"
                                        shape="rounded"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </PageShell>
    );
}